import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import { TimePeriod } from "../../API";
import AllLocationsEventsFilterDropdownContainer from "../../common/components/locations/recent-events/AllLocationsEventsFilterDropdownContainer";
import TabPanel from "../../common/components/tabs/TabPanel";
import { useAuthenticatedUser } from "../../common/hooks/useAuthenticatedUser";
import { AutocompleteOptionType } from "../../common/models/autocomplete";
import {
  StatisticsTabsEnum,
  StatisticsTabsFilters,
  StatisticsTabsList,
} from "../../common/variables/tabsSettings";
import FeedbackInsightsTab from "./tabs/feedback-insights/FeedbackInsightsTab";
import SystemHealthTab from "./tabs/system-health/SystemHealthTab";
import TimePeriodFilter from "./tabs/system-performance/components/TimePeriodFilter";
import SystemPerformanceTab from "./tabs/system-performance/SystemPerformanceTab";

const StatisticsTabs = () => {
  const navigate = useNavigate();

  const { role } = useAuthenticatedUser();

  const { tabId = StatisticsTabsEnum.SYSTEM_PERFORMANCE } = useParams<{
    tabId: StatisticsTabsEnum;
  }>();

  const [selectedLocation, setSelectedLocation] = useState<
    AutocompleteOptionType[] | null
  >(null);

  const [activeTimePeriod, setActiveTimePeriod] = useState<TimePeriod>(
    TimePeriod.ONE_WEEK
  );

  const handleLocationChange = (
    locationValue: AutocompleteOptionType
  ): void => {
    setSelectedLocation([locationValue]);
  };

  const handleLocationTagChange = (
    locationValue: AutocompleteOptionType[] | null
  ): void => {
    setSelectedLocation(locationValue);
  };

  const handleTimePeriodChange = (timePeriod: TimePeriod): void => {
    if (timePeriod !== activeTimePeriod) {
      setActiveTimePeriod(timePeriod);
    }
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: StatisticsTabsEnum
  ): void => {
    navigate(newValue);
  };

  const allowedTabs = StatisticsTabsList.filter((tab): boolean => {
    return tab.allowedRoles.includes(role);
  });

  const tabs = allowedTabs.map((tab): JSX.Element => {
    return (
      <Tab
        key={tab.id}
        sx={{
          textTransform: "none",
        }}
        label={tab.label}
        value={tab.id}
      />
    );
  });

  const showFilters =
    tabId === StatisticsTabsEnum.SYSTEM_HEALTH ||
    tabId === StatisticsTabsEnum.FEEDBACK_INSIGHTS;

  const tabFiltersSettings = StatisticsTabsFilters[tabId];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginBottom: "1.5em",
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
        }}
      >
        <Tabs
          onChange={handleTabChange}
          value={tabId}
          variant="scrollable"
          scrollButtons={false}
        >
          {tabs}
        </Tabs>

        {showFilters && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1em",
              marginLeft: "auto",
            }}
          >
            {tabFiltersSettings?.showLocationFilter && (
              <Box
                sx={{
                  width: "280px",
                }}
              >
                <AllLocationsEventsFilterDropdownContainer
                  setLocation={handleLocationChange}
                  setTagLocations={handleLocationTagChange}
                />
              </Box>
            )}

            {tabFiltersSettings?.showTimePeriodFilter && (
              <TimePeriodFilter
                activePeriod={activeTimePeriod}
                setActivePeriod={handleTimePeriodChange}
              />
            )}
          </Box>
        )}
      </Box>

      <TabPanel value={tabId} index={StatisticsTabsEnum.SYSTEM_PERFORMANCE}>
        <SystemPerformanceTab />
      </TabPanel>

      <TabPanel value={tabId} index={StatisticsTabsEnum.FEEDBACK_INSIGHTS}>
        <FeedbackInsightsTab
          selectedLocation={selectedLocation}
          activeTimePeriod={activeTimePeriod}
        />
      </TabPanel>

      <TabPanel value={tabId} index={StatisticsTabsEnum.SYSTEM_HEALTH}>
        <SystemHealthTab selectedLocation={selectedLocation} />
      </TabPanel>
    </Box>
  );
};

export default StatisticsTabs;
